import React, { useEffect, useRef, useState } from "react";
import config from "../config.js";
import { apiFetch } from "../utils/apiClient.js";
import styles from "./LandingTrust.module.css";

const safeURL = (value) => {
  try { const url = new URL(value); return url.protocol === "https:" ? url.href : null; }
  catch { return null; }
};

export default function LandingTrust() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(false);
  const region = useRef(null);
  const reviews = data?.reviews || [];
  const reviewCount = Number.isSafeInteger(data?.reviewCount) && data.reviewCount >= reviews.length ? data.reviewCount : null;
  const review = reviews[index % (reviews.length || 1)];
  const visits = data?.visits;
  const rate = data?.naver?.acceptRate;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const load = async () => {
      if (document.hidden) return;
      try {
        const response = await apiFetch(`${config.serverUri}/public/landing-trust`, {
          credentials: "include", signal: controller.signal, headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("unavailable");
        const next = await response.json();
        if (!Array.isArray(next.reviews)) throw new Error("invalid response");
        if (active) { setData(next); setStatus("ready"); }
      } catch (error) {
        if (active && error.name !== "AbortError") { setData(null); setStatus("unavailable"); }
      }
    };
    void load();
    const timer = window.setInterval(load, 60000);
    document.addEventListener("visibilitychange", load);
    return () => { active = false; controller.abort(); window.clearInterval(timer); document.removeEventListener("visibilitychange", load); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.3 });
    if (region.current) observer.observe(region.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reviews.length < 2 || paused || interacting || !visible || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex((value) => (value + 1) % reviews.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [reviews.length, paused, interacting, visible]);

  const step = (amount) => { setPaused(true); setIndex((value) => (value + amount + reviews.length) % reviews.length); };
  return <section data-behavior="landing-trust" ref={region} className={styles.section} aria-labelledby="landing-trust-title" data-landing-trust="true">
    <div className={styles.inner}>
      <div className={styles.heading}><h2 id="landing-trust-title">이용 후기</h2>{reviewCount !== null && <span className={styles.reviewCount}>공개 후기 {reviewCount.toLocaleString("ko-KR")}개</span>}</div>
      <div className={styles.card} onMouseEnter={() => setInteracting(true)} onMouseLeave={() => setInteracting(false)}
        onFocus={() => setInteracting(true)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false); }}>
        {review ? <>
          <span className={styles.quote} aria-hidden="true">“</span>
          <blockquote>{review.body}</blockquote>
          <div className={styles.source}>
            {safeURL(review.sourceUrl) ? <a href={safeURL(review.sourceUrl)} target="_blank" rel="noopener noreferrer">{review.sourceLabel} ↗</a> : <span>{review.sourceLabel}</span>}
            {review.reviewedOn && <time dateTime={review.reviewedOn}>{review.reviewedOn}</time>}
          </div>
        </> : <p className={styles.empty}>{status === "loading" ? "후기를 불러오고 있어요." : status === "ready" ? "공개된 후기를 준비하고 있어요." : "지금은 후기를 불러오지 못했어요."}</p>}
        {reviews.length > 1 && <div className={styles.controls}>
          <span aria-live="off">{index % reviews.length + 1} / {reviews.length}</span>
          <button type="button" onClick={() => step(-1)} aria-label="이전 후기"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m14 6-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
          <button type="button" onClick={() => step(1)} aria-label="다음 후기"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m10 6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
          <button type="button" onClick={() => setPaused((value) => !value)} aria-label={paused ? "후기 자동 넘김 시작" : "후기 자동 넘김 멈춤"}><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{paused ? <path d="M8 5.5v13l10-6.5z" /> : <><rect x="7" y="5.5" width="3.5" height="13" rx="1" /><rect x="13.5" y="5.5" width="3.5" height="13" rx="1" /></>}</svg></button>
        </div>}
      </div>
      {(Number.isFinite(rate) || (visits && Number.isSafeInteger(visits.total))) && <div className={styles.metrics}>
        {Number.isFinite(rate) && rate >= 0 && rate <= 100 && <div><span>N사 지식iN 답변 채택률</span><strong>{rate}%</strong></div>}
        {visits && Number.isSafeInteger(visits.total) && visits.total >= 0 && <div><span>누적 방문자수</span><strong>{visits.total.toLocaleString("ko-KR")}명</strong>{visits.since && <small>{visits.since}부터</small>}</div>}
      </div>}
    </div>
  </section>;
}
